import React, { useState, useEffect } from 'react';
import { Box, Typography, Tooltip, useTheme, Card, CardContent, Fade } from '@mui/material';
import PowerIcon from '@mui/icons-material/Power';
import DashboardTitle from '../Common/DashboardTitle';

const PowerHeatmapChart = ({ heatmapData: propHeatmapData }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [heatmapData, setHeatmapData] = useState([]);
  const [visible, setVisible] = useState(false);
  const [maxValue, setMaxValue] = useState(1);

  const TOTAL_WEEKS = 17; // 17주 = 119일 (약 120일)
  const DAYS_PER_WEEK = 7;

  const normalizeDateString = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized.toISOString().split('T')[0];
  };

  const buildBaseGrid = (anchorDate = new Date()) => {
    const endDate = new Date(anchorDate);
    endDate.setHours(0, 0, 0, 0);

    const grid = [];
    for (let week = 0; week < TOTAL_WEEKS; week++) {
      const weekData = [];
      for (let day = 0; day < DAYS_PER_WEEK; day++) {
        const cellDate = new Date(endDate);
        const offset = (TOTAL_WEEKS - week - 1) * DAYS_PER_WEEK + (DAYS_PER_WEEK - day - 1);
        cellDate.setDate(endDate.getDate() - offset);

        const dayOfWeek = cellDate.getDay();
        const dayNames = ['일', '월', '화', '수', '목', '금', '토'];

        weekData.push({
          date: cellDate.toISOString().split('T')[0],
          powerUsage: 0,
          intensity: 0,
          isWeekend: [0, 6].includes(dayOfWeek),
          formattedDate: cellDate.toLocaleDateString('ko-KR', {
            month: 'short',
            day: 'numeric'
          }),
          dayOfWeek: dayNames[dayOfWeek]
        });
      }
      grid.push(weekData);
    }
    return grid;
  };

  const initializeEmptyHeatmap = (anchorDate) => {
    const baseGrid = buildBaseGrid(anchorDate);
    setHeatmapData(baseGrid);
    setMaxValue(1);
  };

  const processBackendHeatmapData = (backendData = []) => {
    try {
      if (!Array.isArray(backendData) || backendData.length === 0) {
        initializeEmptyHeatmap();
        return;
      }

      const validEntries = backendData.filter(item => normalizeDateString(item?.date));
      if (validEntries.length === 0) {
        initializeEmptyHeatmap();
        return;
      }

      const latestEntryDate = validEntries.reduce((latest, current) => {
        const currentDate = new Date(current.date);
        return currentDate > latest ? currentDate : latest;
      }, new Date(validEntries[0].date));

      const grid = buildBaseGrid(latestEntryDate);
      const dataMap = new Map();

      validEntries.forEach(item => {
        const isoDate = normalizeDateString(item.date);
        if (!isoDate) return;

        dataMap.set(isoDate, {
          powerUsage: Number(item.dailyTotalPower ?? 0),
          intensity: Number(item.intensity ?? 0),
          isWeekend: Boolean(item.isWeekend),
          formattedDate: new Date(item.date).toLocaleDateString('ko-KR', {
            month: 'short',
            day: 'numeric'
          })
        });
      });

      let localMax = 0;
      grid.forEach(week => {
        week.forEach(day => {
          const matched = dataMap.get(day.date);
          if (matched) {
            day.powerUsage = matched.powerUsage;
            day.intensity = matched.intensity;
            day.isWeekend = matched.isWeekend;
            day.formattedDate = matched.formattedDate;
            localMax = Math.max(localMax, matched.powerUsage);
          }
        });
      });

      setHeatmapData(grid);
      setMaxValue(localMax > 0 ? localMax : 1);
    } catch (error) {
      console.error('백엔드 히트맵 데이터 처리 실패:', error);
      initializeEmptyHeatmap();
    }
  };

  useEffect(() => {
    setVisible(true);
    if (Array.isArray(propHeatmapData) && propHeatmapData.length > 0) {
      processBackendHeatmapData(propHeatmapData);
    } else {
      initializeEmptyHeatmap();
    }
  }, [propHeatmapData]);

  // 전력 사용량에 따른 색상 강도 계산 (초록색=적게, 빨간색=많이)
  const getHeatColor = (value) => {
    if (!value || maxValue === 0) return isDarkMode ? '#1a1a1a' : '#ebedf0';
    
    const intensity = value / maxValue;
    // 전력 사용량이 많을수록 빨간색, 적을수록 초록색
    if (intensity > 0.8) return '#dc2626'; // 진한 빨간색 (가장 많이)
    if (intensity > 0.6) return '#ef4444'; // 빨간색
    if (intensity > 0.4) return '#fbbf24'; // 주황색 (중간)
    if (intensity > 0.2) return '#a3e635'; // 연두색
    return '#16a34a'; // 초록색 (가장 적게)
  };

  
  // 월 라벨 생성 (주차별로 정확히 매핑)
  const getMonthLabels = () => {
    if (!heatmapData || heatmapData.length === 0) {
      return [];
    }

    const monthInfo = [];
    let currentMonth = null;
    let currentMonthStart = null;
    let currentMonthWeeks = 0;

    heatmapData.forEach((week, weekIndex) => {
      if (!week || week.length === 0) {
        monthInfo.push({ label: '', span: 1 });
        return;
      }

      const weekMonth = new Date(week[0].date).toLocaleDateString('ko-KR', { month: 'short' });

      if (weekMonth !== currentMonth) {
        // 이전 월 정보 저장
        if (currentMonth && currentMonthStart !== null) {
          monthInfo[currentMonthStart] = {
            label: currentMonth,
            span: currentMonthWeeks
          };
        }

        // 새로운 월 시작
        currentMonth = weekMonth;
        currentMonthStart = weekIndex;
        currentMonthWeeks = 1;
        monthInfo.push({ label: '', span: 1 });
      } else {
        currentMonthWeeks++;
        monthInfo.push({ label: '', span: 1 });
      }
    });

    // 마지막 월 정보 저장
    if (currentMonth && currentMonthStart !== null) {
      monthInfo[currentMonthStart] = {
        label: currentMonth,
        span: currentMonthWeeks
      };
    }

    return monthInfo;
  };

  // 통계 계산
  const calculateStats = () => {
    const allValues = heatmapData.flat().map(day => day.powerUsage);
    const total = allValues.reduce((sum, val) => sum + val, 0);
    const average = allValues.length > 0 ? (total / allValues.length).toFixed(1) : 0;
    const maxDay = heatmapData.flat().reduce((max, day) => 
      day.powerUsage > (max?.powerUsage || 0) ? day : max, null);
    
    return { total, average, maxDay };
  };

  const stats = calculateStats();


  return (
    <Fade in={visible} timeout={1000}>
      <Card sx={{
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        bgcolor: '#ffffff',
        border: '1px solid #f0f0f0',
        borderRadius: 3,
        overflow: 'hidden',
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <CardContent sx={{
          p: { xs: 1.5, sm: 2, md: 3 },
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          overflow: 'hidden'
        }}>
          <DashboardTitle
            icon={PowerIcon}
            title="전력 사용량 히트맵"
            subtitle="일일 전력 사용 패턴을 시각적으로 확인하세요"
            size="small"
          />

          {/* Heatmap Grid */}
          <Box sx={{ 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            overflow: 'auto',
            mb: 2
          }}>
            {/* Month Labels */}
            <Box sx={{
              display: 'flex',
              mb: 1,
              ml: { xs: '16px', sm: '22px' },
              gap: { xs: '1px', sm: '2px' },
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {getMonthLabels().map((monthInfo, index) => (
                <Box
                  key={index}
                  sx={{
                    width: { xs: '12px', sm: '16px', md: '20px' },
                    display: 'flex',
                    justifyContent: 'center',
                    minWidth: { xs: '12px', sm: '16px', md: '20px' }
                  }}
                >
                  {monthInfo.label && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#757575',
                        fontSize: { xs: '9px', sm: '10px' },
                        fontWeight: 500,
                        textAlign: 'center',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {monthInfo.label}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>

            {/* Heatmap Grid */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              {/* Heatmap Cells */}
              <Box sx={{
                display: 'flex',
                gap: { xs: '1px', sm: '2px' },
                flexWrap: 'nowrap',
                overflowX: 'auto'
              }}>
                {heatmapData.map((week, weekIndex) => (
                  <Box key={weekIndex} sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: { xs: '1px', sm: '2px' },
                    minWidth: 'max-content'
                  }}>
                    {week.map((day, dayIndex) => (
                      <Tooltip
                        key={`${weekIndex}-${dayIndex}`}
                        title={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {day.formattedDate}({day.dayOfWeek})
                            </Typography>
                            <Typography variant="caption">
                              전력 사용량: {day.powerUsage}kW
                            </Typography>
                          </Box>
                        }
                        arrow
                      >
                        <Box
                          sx={{
                            width: { xs: '12px', sm: '16px', md: '20px' },
                            height: { xs: '12px', sm: '16px', md: '20px' },
                            backgroundColor: getHeatColor(day.powerUsage),
                            borderRadius: '3px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            border: '1px solid rgba(27,31,35,0.06)',
                            '&:hover': {
                              zIndex: 1,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                              opacity: 0.8
                            }
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {/* Legend and Stats */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {/* Legend */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ color: '#757575', fontSize: { xs: '11px', sm: '12px' }, fontWeight: 500 }}>
                적음
              </Typography>
              {[0, 0.2, 0.4, 0.6, 0.8].map((intensity, index) => (
                <Box
                  key={index}
                  sx={{
                    width: '12px',
                    height: '12px',
                    backgroundColor: getHeatColor(intensity * maxValue),
                    borderRadius: '2px',
                    border: '1px solid rgba(27,31,35,0.06)'
                  }}
                />
              ))}
              <Typography variant="caption" sx={{ color: '#757575', fontSize: { xs: '11px', sm: '12px' }, fontWeight: 500 }}>
                많음
              </Typography>
            </Box>

            {/* Stats */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Typography variant="caption" sx={{ color: '#757575', fontSize: { xs: '11px', sm: '12px' } }}>
                평균: <strong>{stats.average}kW</strong>
              </Typography>
              <Typography variant="caption" sx={{ color: '#757575', fontSize: { xs: '11px', sm: '12px' } }}>
                최대: <strong>{stats.maxDay?.powerUsage || 0}kW</strong>
              </Typography>
              <Typography variant="caption" sx={{ color: '#757575', fontSize: { xs: '11px', sm: '12px' } }}>
                총 데이터: <strong>{heatmapData.flat().length}일</strong>
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Fade>
  );
};

export default PowerHeatmapChart;
